<template>
  <section class="card">
    <div class="page-head">
      <div class="page-head-left">
    <button
      class="back-btn"
      type="button"
      title="返回首页入口管理"
      @click="goBackToTiles"
      @mouseenter="backBtnHover = true"
      @mouseleave="backBtnHover = false"
    >
      <img
        class="back-btn-icon"
        :src="backBtnHover ? backAboutActiveIcon : backAboutIcon"
        alt="返回"
      >
    </button>
    <h2>专业会议页管理</h2>
      </div>
      <button class="btn btn-primary" :disabled="saving" @click="save">
        {{ saving ? '保存中...' : '保存' }}
      </button>
    </div>
    <p class="tip">
      说明：这里配置「专业会议」顶部标签与条目。库里的 <code>content_key</code> 可为
      <code>meeting</code> 或旧版 <code>meeting_tabs</code>；小程序按实际 Key 请求接口。浏览量由打开条目时上报统计。
    </p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="okMsg" class="ok">{{ okMsg }}</p>

    <div class="panel tile-panel">
      <h3>首页入口（专业会议）</h3>
      <div class="tile-grid">
        <label class="field inline">
          <span>入口标题</span>
          <input v-model="tileForm.title" placeholder="例如：专业会议" />
        </label>
        <label class="field inline">
          <span>排序值（只读）</span>
          <input :value="tileForm.sortOrder || 0" disabled />
        </label>
        <div class="field inline tile-upload">
          <span>入口图片</span>
          <div class="img-row">
            <div class="upload-box upload-box--sm" @click="pickTileImage" @contextmenu.prevent="pickTileImage">
              <img v-if="tileForm.imageUrl" class="upload-img" :src="toFullUrl(tileForm.imageUrl)" alt="" />
              <div v-else class="upload-placeholder">
                <div class="upload-plus">+</div>
                <div class="upload-text">点击上传入口图</div>
              </div>
            </div>
            <div class="upload-hint">提示：点击图片可更改。</div>
          </div>
          <input ref="tileImageInput" class="hidden-input" type="file" accept="image/*" @change="onTileImageChange" />
        </div>
        <label class="field inline">
          <span>入口状态</span>
          <FancySelect v-model="tileForm.statusCode" :options="statusOptions" icon="●" />
        </label>
        <label class="field inline">
          <span>页面类型</span>
          <FancySelect
            v-model="tileForm.pageTypeId"
            :options="pageTypeOptions"
            icon="📄"
            placeholder="不标注类型"
            :searchable="true"
          />
        </label>
      </div>
    </div>

    <div class="topbar">
      <label class="field inline">
        <span>状态</span>
        <FancySelect v-model="statusCode" :options="statusOptions" icon="●" />
      </label>
      <label class="field inline">
        <span>页面标题</span>
        <input v-model="title" placeholder="例如：专业会议" />
      </label>
    </div>

    <div class="grid">
      <div class="panel">
        <div class="panel-head">
          <h3>顶部导航栏（标签）</h3>
          <button class="btn btn-edit" @click="addTab">新增标签</button>
        </div>
        <p class="drag-hint">提示：按住每行左侧“拖拽”即可调整标签顺序。</p>

        <div v-if="!meetingTabs.length" class="empty">还没有标签，请先新增。</div>
        <div
          v-for="(tab, idx) in meetingTabs"
          :key="tab._k"
          class="tab-item"
          :class="{ active: selectedTabIndex === idx }"
          @click="selectedTabIndex = idx"
          draggable="true"
          @dragstart="onTabDragStart(idx)"
          @dragover.prevent
          @drop="onTabDrop(idx)"
          @dragend="onTabDragEnd"
        >
          <span class="drag-handle" title="拖拽排序">⋮⋮</span>
          <span class="tab-index">{{ idx + 1 }}</span>
          <input
            v-model="tab.label"
            class="tab-label"
            placeholder="例如：会议通知"
            @click.stop
          />
          <button class="btn btn-delete" @click.stop="removeTab(idx)">删</button>
        </div>
      </div>

      <div class="panel">
        <div class="panel-head">
          <h3>标签内容（推文条目）</h3>
          <button class="btn btn-edit" :disabled="selectedTabIndex < 0" @click="openItemModalAdd">新增条目</button>
        </div>

        <p v-if="selectedTabIndex < 0" class="empty">请先在左侧新增并选择一个标签。</p>
        <template v-else>
          <p class="subtip">当前标签：{{ currentTab.label || `标签${selectedTabIndex + 1}` }}。新增或编辑在弹窗中完成；列表仅展示摘要，可拖拽排序。</p>
          <p class="drag-hint">提示：按住每行左侧“拖拽”可调整当前标签内条目顺序。</p>
          <div v-if="!currentItems.length" class="empty">当前标签还没有条目，点击右上角「新增条目」。</div>

          <div
            v-for="(it, i) in currentItems"
            :key="it._k"
            class="item-row"
            draggable="true"
            @dragstart="onItemDragStart(i)"
            @dragover.prevent
            @drop="onItemDrop(i)"
            @dragend="onItemDragEnd"
          >
            <span class="drag-handle" title="拖拽排序">⋮⋮</span>
            <span class="item-row-index">{{ i + 1 }}</span>
            <div class="item-row-main">
              <div class="item-row-title">{{ it.title || '（未填写标题）' }}</div>
              <div class="item-row-meta">
                <span v-if="it.date">{{ it.date }}</span>
                <span v-if="it.wechatUrl" class="item-row-link">已填链接</span>
              </div>
            </div>
            <div class="item-row-actions" @click.stop>
              <button type="button" class="btn btn-edit btn-sm" @click="openItemModalEdit(i)">编辑</button>
              <button type="button" class="btn btn-delete btn-sm" @click="removeItem(i)">删除</button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 新增 / 编辑条目弹窗 -->
    <div v-if="itemModalVisible" class="modal-mask" @click.self="closeItemModal">
      <div class="modal-dialog" role="dialog" aria-modal="true" @click.stop>
        <div class="modal-head">
          <h4 class="modal-title">{{ itemModalMode === 'add' ? '新增条目' : '编辑条目' }}</h4>
          <button type="button" class="btn-modal-close" title="关闭" @click="closeItemModal">×</button>
        </div>
        <div class="modal-body">
          <label class="field">
            <span>标题</span>
            <input v-model="itemForm.title" placeholder="例如：2026 专业会议通知" />
          </label>
          <label class="field">
            <span>发布日期</span>
            <input v-model="itemForm.date" type="date" />
          </label>
          <label class="field">
            <span>摘要</span>
            <textarea v-model="itemForm.excerpt" placeholder="可选，列表与详情占位文案" rows="3" />
          </label>
          <label class="field">
            <span>公众号/网页链接</span>
            <input v-model="itemForm.wechatUrl" type="url" placeholder="https://mp.weixin.qq.com/..." />
          </label>
          <div class="field">
            <span>封面图</span>
            <div class="img-row">
              <div
                class="upload-box upload-box--sm"
                @click="pickItemModalThumb"
                @contextmenu.prevent="pickItemModalThumb"
              >
                <img v-if="itemForm.thumb" class="upload-img" :src="toFullUrl(itemForm.thumb)" alt="" />
                <div v-else class="upload-placeholder">
                  <div class="upload-plus">+</div>
                  <div class="upload-text">点击上传封面</div>
                </div>
              </div>
              <div class="upload-hint">提示：点击图片可更改。</div>
            </div>
          </div>
          <input
            ref="itemModalThumbInput"
            class="hidden-input"
            type="file"
            accept="image/*"
            @change="onItemModalThumbChange"
          />
        </div>
        <div class="modal-foot">
          <button type="button" class="btn btn-edit" @click="closeItemModal">取消</button>
          <button type="button" class="btn btn-primary" :disabled="itemThumbUploading" @click="confirmItemModal">
            确定
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import FancySelect from '@/components/FancySelect.vue'
import {
  createPageContent,
  getPageContentById,
  getPageContents,
  getTilePageTypes,
  getTiles,
  toMediaUrl,
  updatePageContent,
  updateTile,
  uploadImage,
} from '@/api'
import { isMeetingStructuredKey } from './pageContentKeys.js'
import backAboutIcon from '@/assets/icons/back-about.svg'
import backAboutActiveIcon from '@/assets/icons/back-about-active.svg'

function safeJsonParse(s, fallback) {
  try {
    return JSON.parse(s)
  } catch {
    return fallback
  }
}

function genKey() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`
}

function genStableItemId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}_${Math.random().toString(16).slice(2)}`
}

function createItem() {
  return {
    _k: genKey(),
    id: genStableItemId(),
    title: '',
    excerpt: '',
    date: '',
    thumb: '',
    wechatUrl: '',
    fileUrl: ''
  }
}

function emptyItemForm() {
  return {
    title: '',
    excerpt: '',
    date: '',
    thumb: '',
    wechatUrl: '',
    fileUrl: ''
  }
}

export default {
  name: 'MeetingTabsManageView',
  components: { FancySelect },
  props: {
    pageRowId: { type: Number, default: null }
  },
  data() {
    return {
      errorMsg: '',
      okMsg: '',
      saving: false,
      rowId: null,
      title: '专业会议',
      statusCode: 'ENABLED',
      statusOptions: [
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' }
      ],
      meetingTabs: [],
      selectedTabIndex: -1,
      draggingTabIndex: -1,
      draggingItemIndex: -1,
      itemModalVisible: false,
      itemModalMode: 'add',
      itemEditIndex: -1,
      itemForm: emptyItemForm(),
      itemThumbUploading: false,
      persistedContentKey: 'meeting',
      tileId: null,
      backBtnHover: false,
      backAboutIcon,
      backAboutActiveIcon,
      tileRaw: null,
      pageTypeOptions: [{ value: '', label: '（不标注类型）' }],
      tileForm: {
        title: '专业会议',
        imageUrl: '',
        statusCode: 'ENABLED',
        pageTypeId: '',
        sortOrder: 0,
      },
    }
  },
  computed: {
    currentTab() {
      if (this.selectedTabIndex < 0 || this.selectedTabIndex >= this.meetingTabs.length) return null
      return this.meetingTabs[this.selectedTabIndex]
    },
    currentItems() {
      return this.currentTab && Array.isArray(this.currentTab.items) ? this.currentTab.items : []
    }
  },
  mounted() {
    this.loadPageTypeOptions()
    this.load()
  },
  methods: {
    async loadPageTypeOptions() {
      try {
        const rows = (await getTilePageTypes({ status: 'ENABLED' })) || []
        this.pageTypeOptions = [
          { value: '', label: '（不标注类型）' },
          ...rows.map((x) => ({ value: String(x.id), label: String(x.pageName || '') })).filter((x) => x.label)
        ]
      } catch {
        this.pageTypeOptions = [{ value: '', label: '（不标注类型）' }]
      }
    },
    goBackToTiles() {
      this.$router.push('/cms/home/tile')
    },
    async loadMeetingTile() {
      const rows = (await getTiles()) || []
      const queryTileId = Number(this.$route?.query?.tileId || 0)
      let tile = null
      if (queryTileId > 0) {
        tile = rows.find((x) => Number(x.id) === queryTileId) || null
      }
      if (!tile) {
        tile = rows.find((x) => {
          const code = String(x?.linkTarget || '')
          const title = String(x?.title || '').trim()
          return /[?&]code=meeting(?:&|$)/.test(code) || title === '专业会议'
        }) || null
      }
      if (!tile) {
        this.errorMsg = '未找到“专业会议”首页入口，请先在首页入口管理中创建。'
        return
      }
      this.tileId = Number(tile.id)
      this.tileRaw = { ...tile }
      this.tileForm = {
        title: String(tile.title || '专业会议'),
        imageUrl: String(tile.imageUrl || ''),
        statusCode: String(tile.statusCode || 'ENABLED'),
        pageTypeId: tile.pageTypeId != null && tile.pageTypeId !== '' ? String(tile.pageTypeId) : '',
        sortOrder: Number(tile.sortOrder || 0),
      }
    },
    /** 上传接口常返回 /uploads/... 相对路径；后台页面跑在 Vite 端口，img 需指向后端域名才能预览 */
    toFullUrl(url) {
      const u = String(url || '').trim()
      if (!u) return ''
      if (u.startsWith('data:')) return u
      return toMediaUrl(u)
    },
    async load() {
      this.errorMsg = ''
      this.okMsg = ''
      try {
        await this.loadMeetingTile()
        let row = null
        const pid = this.pageRowId != null ? Number(this.pageRowId) : NaN
        if (!Number.isNaN(pid) && pid > 0) {
          row = await getPageContentById(pid)
          const ck = String(row && row.contentKey ? row.contentKey : '').trim()
          if (row && !isMeetingStructuredKey(ck)) {
            this.errorMsg = '该记录不是专业会议类型（content_key 应为 meeting 或 meeting_tabs）'
            return
          }
        } else {
          const list = (await getPageContents()) || []
          row = list.find(
            (it) =>
              isMeetingStructuredKey(it.contentKey) &&
              (it.isDeleted === 0 || typeof it.isDeleted === 'undefined')
          )
        }
        if (!row) return

        this.persistedContentKey = String(row.contentKey || 'meeting').trim() || 'meeting'
        this.rowId = row.id
        this.title = row.title || '专业会议'
        this.statusCode = row.statusCode || 'ENABLED'

        const cfg = safeJsonParse(row.content || '', null)
        if (!cfg || cfg.variant !== 'thumbListTabs') return
        const tabs = Array.isArray(cfg.meetingTabs) ? cfg.meetingTabs : []
        this.meetingTabs = tabs.map((t, idx) => ({
          _k: genKey(),
          id: t.id || `tab-${idx + 1}`,
          label: t.label || '',
          items: (Array.isArray(t.items) ? t.items : []).map((it, i2) => {
            const base = createItem()
            const merged = { ...base, ...it, _k: genKey() }
            merged.id = (it.id && String(it.id).trim()) || base.id || `${idx + 1}-${i2 + 1}`
            delete merged.views
            return merged
          })
        }))
        this.selectedTabIndex = this.meetingTabs.length ? 0 : -1
      } catch (e) {
        this.errorMsg = e.message || '读取失败'
      }
    },
    addTab() {
      const idx = this.meetingTabs.length + 1
      this.meetingTabs = [
        ...this.meetingTabs,
        { _k: genKey(), id: `tab-${idx}`, label: `标签${idx}`, items: [] }
      ]
      this.selectedTabIndex = this.meetingTabs.length - 1
    },
    removeTab(idx) {
      const arr = [...this.meetingTabs]
      arr.splice(idx, 1)
      this.meetingTabs = arr
      if (!this.meetingTabs.length) {
        this.selectedTabIndex = -1
      } else if (this.selectedTabIndex >= this.meetingTabs.length) {
        this.selectedTabIndex = this.meetingTabs.length - 1
      }
    },
    onTabDragStart(idx) {
      this.draggingTabIndex = idx
    },
    onTabDrop(dropIdx) {
      const from = this.draggingTabIndex
      this.draggingTabIndex = -1
      if (from < 0 || from === dropIdx) return
      const arr = [...this.meetingTabs]
      const moved = arr.splice(from, 1)[0]
      arr.splice(dropIdx, 0, moved)
      this.meetingTabs = arr
      this.selectedTabIndex = dropIdx
    },
    onTabDragEnd() {
      this.draggingTabIndex = -1
    },
    openItemModalAdd() {
      if (this.selectedTabIndex < 0) return
      this.errorMsg = ''
      this.itemModalMode = 'add'
      this.itemEditIndex = -1
      this.itemForm = emptyItemForm()
      this.itemModalVisible = true
    },
    openItemModalEdit(i) {
      if (this.selectedTabIndex < 0) return
      const it = this.currentItems[i]
      if (!it) return
      this.errorMsg = ''
      this.itemModalMode = 'edit'
      this.itemEditIndex = i
      this.itemForm = {
        title: it.title || '',
        excerpt: it.excerpt || '',
        date: it.date || '',
        thumb: it.thumb || '',
        wechatUrl: it.wechatUrl || '',
        fileUrl: it.fileUrl || ''
      }
      this.itemModalVisible = true
    },
    closeItemModal() {
      if (this.itemThumbUploading) return
      this.itemModalVisible = false
      this.itemEditIndex = -1
    },
    confirmItemModal() {
      const title = (this.itemForm.title || '').trim()
      if (!title) {
        this.errorMsg = '请填写标题'
        return
      }
      if (this.selectedTabIndex < 0) return

      const arr = [...this.meetingTabs]
      const tab = { ...arr[this.selectedTabIndex] }
      const items = [...(tab.items || [])]

      const payload = {
        title,
        excerpt: (this.itemForm.excerpt || '').trim(),
        date: (this.itemForm.date || '').trim(),
        thumb: (this.itemForm.thumb || '').trim(),
        wechatUrl: (this.itemForm.wechatUrl || '').trim(),
        fileUrl: (this.itemForm.fileUrl || '').trim()
      }

      if (this.itemModalMode === 'add') {
        const row = createItem()
        items.push({ ...row, ...payload })
      } else {
        const i = this.itemEditIndex
        if (i < 0 || i >= items.length) return
        const prev = items[i]
        items[i] = { ...prev, ...payload }
      }

      tab.items = items
      arr[this.selectedTabIndex] = tab
      this.meetingTabs = arr
      this.errorMsg = ''
      this.itemModalVisible = false
      this.itemEditIndex = -1
    },
    removeItem(i) {
      if (this.selectedTabIndex < 0) return
      const arr = [...this.meetingTabs]
      const tab = { ...arr[this.selectedTabIndex] }
      const items = [...(tab.items || [])]
      items.splice(i, 1)
      tab.items = items
      arr[this.selectedTabIndex] = tab
      this.meetingTabs = arr
    },
    onItemDragStart(i) {
      this.draggingItemIndex = i
    },
    onItemDrop(dropIdx) {
      const from = this.draggingItemIndex
      this.draggingItemIndex = -1
      if (this.selectedTabIndex < 0 || from < 0 || from === dropIdx) return
      const arr = [...this.meetingTabs]
      const tab = { ...arr[this.selectedTabIndex] }
      const items = [...(tab.items || [])]
      const moved = items.splice(from, 1)[0]
      items.splice(dropIdx, 0, moved)
      tab.items = items
      arr[this.selectedTabIndex] = tab
      this.meetingTabs = arr
    },
    onItemDragEnd() {
      this.draggingItemIndex = -1
    },
    pickItemModalThumb() {
      const input = this.$refs.itemModalThumbInput
      if (input) input.click()
    },
    async onItemModalThumbChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.itemThumbUploading = true
      this.errorMsg = ''
      try {
        const data = await uploadImage(file)
        const url = String((data && (data.url || data.fileUrl || data.path)) || '').trim()
        if (!url) throw new Error('未获取到图片URL')
        this.itemForm = { ...this.itemForm, thumb: url }
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      } finally {
        this.itemThumbUploading = false
        e.target.value = ''
      }
    },
    buildPayload() {
      const meetingTabs = (this.meetingTabs || [])
        .map((t, idx) => ({
          id: (t.id || `tab-${idx + 1}`).trim(),
          label: (t.label || '').trim(),
          items: (Array.isArray(t.items) ? t.items : [])
            .map((it, i2) => ({
              id: (it.id || `${idx + 1}-${i2 + 1}`).trim(),
              title: (it.title || '').trim(),
              excerpt: (it.excerpt || '').trim(),
              date: (it.date || '').trim(),
              thumb: it.thumb || '',
              wechatUrl: (it.wechatUrl || '').trim(),
              fileUrl: (it.fileUrl || '').trim()
            }))
            .filter((x) => x.title)
        }))
        .filter((t) => t.label)

      return {
        contentKey: this.persistedContentKey || 'meeting',
        title: (this.title || '专业会议').trim(),
        content: JSON.stringify({
          variant: 'thumbListTabs',
          meetingTabs
        }),
        statusCode: this.statusCode,
        remark: '专业会议多标签配置（JSON）'
      }
    },
    async save() {
      this.errorMsg = ''
      this.okMsg = ''
      this.saving = true
      try {
        if (!this.tileId) throw new Error('专业会议入口不存在，无法保存。')
        if (!String(this.tileForm.title || '').trim()) throw new Error('请填写入口标题')
        if (!String(this.tileForm.imageUrl || '').trim()) throw new Error('请上传入口图片')
        const raw = this.tileRaw || {}
        await updateTile(this.tileId, {
          ...raw,
          pageTypeId: this.tileForm.pageTypeId ? Number(this.tileForm.pageTypeId) : null,
          linkTarget: String(raw.linkTarget || '/pages/article/list?code=meeting'),
          title: String(this.tileForm.title || '').trim(),
          imageUrl: String(this.tileForm.imageUrl || '').trim(),
          statusCode: String(this.tileForm.statusCode || 'ENABLED'),
          sortOrder: Number(this.tileForm.sortOrder || 0),
        })
        const payload = this.buildPayload()
        if (!payload.content || !JSON.parse(payload.content).meetingTabs.length) {
          throw new Error('请至少保留一个标签')
        }
        if (this.rowId) {
          await updatePageContent(this.rowId, payload)
        } else {
          const created = await createPageContent(payload)
          this.rowId = created?.id || this.rowId
        }
        this.okMsg = '保存成功'
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    pickTileImage() {
      const input = this.$refs.tileImageInput
      if (input) input.click()
    },
    async onTileImageChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = data.url || ''
        if (!url) throw new Error('未获取到图片URL')
        this.tileForm.imageUrl = url
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      } finally {
        e.target.value = ''
      }
    }
  }
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 18px; }
.card * { box-sizing: border-box; }
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.page-head-left {
  display: flex;
  flex-direction: column;
}
.back-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-bottom: 8px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform .15s ease;
}
.back-btn:hover { transform: translateX(-1px); }
.back-btn-icon {
  width: 32px;
  height: 32px;
  display: block;
}
.tip { background: #f5f8ff; border: 1px solid #dae5ff; color: #2a4b8d; padding: 10px 12px; border-radius: 8px; margin-bottom: 10px; line-height: 1.6; }
.error { color: #d92d20; margin: 8px 0; }
.ok { color: #067647; margin: 8px 0; }

.topbar { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; margin: 12px 0 16px; }
.grid { display: grid; grid-template-columns: minmax(0, 320px) minmax(0, 1fr); gap: 14px; }
@media (max-width: 1080px) { .grid { grid-template-columns: 1fr; } }

.panel { border: 1px solid #eef2f7; border-radius: 12px; padding: 14px; background: #fff; min-width: 0; }
.panel-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
.panel h3 { margin: 0; font-size: 14px; color: #101828; }
.tile-panel { margin-bottom: 12px; }
.tile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.tile-upload { grid-column: span 2; }
.subtip { color: #98a2b3; font-size: 12px; margin: 0 0 10px; }
.drag-hint { color: #667085; font-size: 12px; margin: 0 0 10px; }
.empty { color: #98a2b3; font-size: 13px; padding: 8px 0; }

.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; margin-bottom: 10px; min-width: 0; }
.field.inline { min-width: 220px; margin-bottom: 0; }
.field > span { font-weight: 700; }
.field input, .field textarea { border: 1px solid #d0d7e2; border-radius: 10px; padding: 9px 10px; font-size: 13px; width: 100%; }
.field textarea { min-height: 72px; resize: vertical; }
.full { grid-column: span 2; }

.btn { border-radius: 10px; padding: 8px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #ffc8c2; background: #fff1f0; color: #d92d20; }
.btn:disabled { opacity: .6; cursor: not-allowed; }

.tab-item {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}
.tab-item.active { border-color: #9bb7ff; background: #f5f8ff; }
.drag-handle {
  color: #98a2b3;
  font-size: 14px;
  line-height: 1;
  user-select: none;
  cursor: grab;
}
.tab-index {
  width: 20px;
  height: 20px;
  border-radius: 8px;
  background: #eef4ff;
  color: #2f6af6;
  font-size: 12px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tab-label { flex: 1; min-width: 0; }

.item-row {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 8px;
  background: #fbfdff;
}
.item-row-index {
  width: 22px;
  height: 22px;
  border-radius: 8px;
  background: #eef4ff;
  color: #2f6af6;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.item-row-main { flex: 1; min-width: 0; }
.item-row-title {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.item-row-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #98a2b3;
  display: flex;
  align-items: center;
  gap: 10px;
}
.item-row-link { color: #2f6af6; }
.item-row-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.btn-sm { padding: 6px 10px; font-size: 12px; }

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.modal-dialog {
  width: 100%;
  max-width: 520px;
  max-height: min(90vh, 720px);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
}
.modal-title { margin: 0; font-size: 16px; color: #101828; }
.btn-modal-close {
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #98a2b3;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
}
.btn-modal-close:hover { background: #f2f4f7; color: #344054; }
.modal-body {
  padding: 14px 16px;
  overflow-y: auto;
  flex: 1;
}
.modal-body .field { margin-bottom: 12px; }
.modal-body .field:last-child { margin-bottom: 0; }
.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #eef2f7;
  background: #fafbfc;
}

.img-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.upload-box {
  width: 180px;
  height: 110px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  transition: border-color .18s ease, background .18s ease, box-shadow .18s ease;
}
.upload-box:hover {
  border-color: #98b4ff;
  background: #f3f7ff;
  box-shadow: 0 8px 20px rgba(47, 106, 246, 0.12);
}
.upload-box--sm { width: 180px; height: 110px; }
.upload-img { width: 100%; height: 100%; object-fit: cover; }
.upload-placeholder {
  color: #98a2b3;
  font-size: 12px;
  padding: 8px 10px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.upload-plus {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid #c8d7ff;
  background: #fff;
  color: #2f6af6;
  font-size: 18px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.upload-text { color: #667085; font-size: 12px; }
.upload-hint { color: #98a2b3; font-size: 12px; }
.hidden-input { display: none; }
</style>

